import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import { AdminService } from '../../../shared/services/admin.service';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

type FilterOption = { key: any; value: any };

@Component({
  selector: 'app-mydownloads',
  templateUrl: './mydownloads.component.html',
  styleUrls: ['./mydownloads.component.css'],
  standalone: false
})
export class MydownloadsComponent implements OnInit {
  showLoader: boolean = false;
  mydownloads_dataSource = new MatTableDataSource<any>([]);
  searchField: string = '';
  isRefreshing: boolean = false;  // Track refresh button state
  private isManualRefresh: boolean = false;  // Track if this is a manual refresh vs initial load

  private toUtcDateOrNull(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      // If backend doesn't include timezone, treat as UTC.
      const hasTimeZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed);
      if (!hasTimeZone) {
        const isoLike = trimmed.includes(' ') ? trimmed.replace(' ', 'T') : trimmed;
        const dateUtc = new Date(`${isoLike}Z`);
        return isNaN(dateUtc.getTime()) ? null : dateUtc;
      }
    }

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }



  // NOTE: app-maintenance-table-v4 shows filter icons on sortable headers only for specific keys.
  // To get filter + sort on every column without changing the reusable component, we map our API fields
  // into these supported keys.
  TABLE_COLUMNS: any[] = [
    {
      key: 'Service',
      labelKey: 'Service',
      label: 'File',
      sortable: true,
      filter: true,
      search: '',
      searchValue: [],
      edit: false,
      validations: [{ required: false }],
      width: '25% !important'
    },
    {
      key: 'Category',
      labelKey: 'Category',
      label: 'Created On',
      sortable: true,
      filter: true,
      type: 'date',
      dateFormat: 'medium',
      timeZone: 'UTC',
      timeZoneLabel: 'GMT',
      sortAccessor: 'created_at',
      search: '',
      searchValue: [],
      edit: false,
      validations: [{ required: false }]
    },
    {
      key: 'UI_Comments',
      labelKey: 'UI_Comments',
      label: 'Expires On',
      sortable: true,
      filter: true,
      type: 'date',
      dateFormat: 'medium',
      timeZone: 'UTC',
      timeZoneLabel: 'GMT',
      sortAccessor: 'Expires_on',
      search: '',
      searchValue: [],
      edit: false,
      validations: [{ required: false }]
    },
    {
      key: 'Complex_Services',
      labelKey: 'Complex_Services',
      label: 'Created By',
      sortable: true,
      filter: true,
      search: '',
      searchValue: [],
      edit: false,
      validations: [{ required: false }]
    },
    {
      key: 'Public_Private_Cloud',
      labelKey: 'Public_Private_Cloud',
      label: 'Size',
      sortable: true,
      filter: true,
      search: '',
      searchValue: [],
      edit: false,
      validations: [{ required: false }]
    },
    {
      key: 'SRVDESC',
      labelKey: 'SRVDESC',
      label: 'Status',
      sortable: true,
      filter: true,
      action: 'download',
      search: '',
      searchValue: [],
      edit: false,
      validations: [{ required: false }],
      width: '6% !important'
    },

  ];


  TABLE_CONFIG: any = {
    // pageSize: [10, 20, 30],
    // pageIndex: 0,
    totalItems: 0,
    selection: false,
    commonEdit: false,
    commonEditLabel: 'Click to Edit',
    edit: false,
    serverSidePagination: true,
    deleteFeature: false,
    expandable: false,
    TABLE_CLASS: ''
  };

  // Schema is mainly used for edit-mode inputs; for My Downloads we keep it simple.
  DATA_SCHEMA: any = {
    Service: 'text',
    Category: 'text',
    UI_Comments: 'text',
    Complex_Services: 'text',
    Public_Private_Cloud: 'text',
    SRVDESC: 'text'
  };

  sortConfig = {
    active: 'created_at',
    direction: 'desc'
  };
  constructor(public admnsrv: AdminService, public toaster: ToastrService) {

  }

  ngOnInit() {
    this.getMyDownload();
  }

  refreshData(event: MouseEvent) {
    // (event.target as HTMLButtonElement).blur();
    if (this.isRefreshing) {
      return; // Prevent multiple simultaneous refresh calls
    }
    // Hard Refresh: Clear all filters and search
    this.searchField = '';  // Clear search field
    this.mydownloads_dataSource.filter = '';  // Clear filter
    
    // Clear all column filter search values to prevent old filters from persisting
    this.TABLE_COLUMNS.forEach(col => {
      col.search = '';
      col.searchValue = [];
    });
    
    // Reset sort to default (Created On, descending)
    this.sortConfig = {
      active: 'created_at',
      direction: 'desc'
    };
    
    this.isRefreshing = true;  // Set button state to "Refreshing..."
    this.isManualRefresh = true;  // Flag that this is a manual refresh
    this.getMyDownload();
  }

   info() {
      window.open(environment.docUrl + 'user-guide/My-Downloads/' );
    }

  searchDataSet(_: KeyboardEvent) {
    const filterValue = (this.searchField ?? '').toString().trim().toLowerCase();

    this.mydownloads_dataSource.filterPredicate = (row: any, filter: string) => {
      if (!filter) return true;

      const normalize = (value: any) =>
        value
          .toString()
          .toLowerCase()
          .replace(/\u00a0/g, ' ') // nbsp -> space
          .replace(/\s+/g, ' ') // collapse whitespace
          .trim();

      const compact = (value: any) => normalize(value).replace(/[^a-z0-9]/g, '');

      const parts = [row?.Service]
        .filter(v => v !== null && v !== undefined)
        .map(v => v.toString());

      const joined = parts.join(' ');
      const textHaystack = normalize(joined);
      const compactHaystack = compact(joined);

      const normalizedFilter = normalize(filter);
      const compactFilter = compact(filter);

      // 1) Normal text match (works for normal strings)
      // 2) Compact match (helps when searching numbers/units like "224kb" vs "224 KB")
      return textHaystack.includes(normalizedFilter) || compactHaystack.includes(compactFilter);
    };

    this.mydownloads_dataSource.filter = filterValue;
    this.updateFileCount();
  }

  // Update file count based on current filtered data
  updateFileCount() {
    this.TABLE_CONFIG.totalItems = this.mydownloads_dataSource.filteredData?.length ?? 0;
  }

  onFilteredDataCountChange(count: number) {
    // Update the count when filters are applied from the table component
    this.TABLE_CONFIG.totalItems = count;
  }

  getMyDownload() {
    
    this.admnsrv.getMyDownload('list').subscribe((response: any) => {
        this.mydownloads_dataSource = new MatTableDataSource<any>([]);
      const apiRows = Array.isArray(response) ? response : [];
      const rows = apiRows.map(r => {
        const createdAtUtc = this.toUtcDateOrNull(r?.created_at);
        const expiresOnUtc = this.toUtcDateOrNull(r?.Expires_on);

        return {
          // keep originals in case needed later
          ...r,

          // Map API response keys -> supported table keys (override any same-named keys from API)
          Service: r?.blobfilename ? this.updateReportName(r.blobfilename) : '',
          Category: createdAtUtc,
          UI_Comments: expiresOnUtc,
          Complex_Services: r?.created_by_name ? `${r?.created_by_name} (${r?.created_by})` : r?.created_by ?? '',
          Size: r?.filesizeReadable ?? '',
          Public_Private_Cloud: r?.filesizeReadable ?? '',
          SRVDESC: r?.file_status ?? '',
          created_by: r?.created_by
        };
      });

      this.mydownloads_dataSource = new MatTableDataSource<any>(rows);

      this.TABLE_CONFIG.totalItems = rows.length;

  

      // Show success toaster only for manual refresh (not on initial load)
      if (this.isManualRefresh) {
        // Try showing toaster without delay
        this.toaster.success('Data refreshed successfully', '', {
          timeOut: 3000,
          positionClass: 'toast-top-center',
          enableHtml: false
        });
        this.isManualRefresh = false;
        this.isRefreshing = false;
      }
    },
      (err) => {
        this.isRefreshing = false;  // Reset button state on error
        this.isManualRefresh = false;  // Reset flag on error too
        this.showErrMsg("Something Went Wrong. Please try again.");
      })
  }

  showErrMsg(err: any) {
    const message = err;
    this.toaster.error(message, '', {
      timeOut: 3000,
      positionClass: 'toast-top-center'
    });
  }

  // TODO: Hook this up to the download API later.
  async onDownloadClick(event: any) {
    const row = event?.row ?? event;
    const done: (() => void) | undefined = typeof event?.done === 'function' ? event.done : undefined;

    this.showLoader = true;
    const blobFile = {

      blobPath: row?.blobfilepath,
      fileName: row?.blobfilepath + '/' + row?.blobfilename,
    }

    try {
      const response = await firstValueFrom(this.admnsrv.downloadBlobFile(blobFile));

      const blob = response?.body;
      if (!blob) {
        this.toaster.error('Download failed: empty file response', '', {
          timeOut: 5000,
          positionClass: 'toast-top-center'
        });
        return;
      }

      const filename = this.getFilenameFromContentDisposition(
        response.headers?.get('Content-Disposition')
      ) || row?.blobfilename || 'download';

      // IE/legacy Edge fallback
      const navAny = window.navigator as any;
      if (navAny?.msSaveOrOpenBlob) {
        navAny.msSaveOrOpenBlob(blob, filename);
        return;
      }

      const objecturl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objecturl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after a tick to avoid cutting off the download in some browsers
      setTimeout(() => window.URL.revokeObjectURL(objecturl), 0);
      this.updateMyDownload(row, blob);
    } catch (err: any) {
      this.showErrMsg("Download failed. Please try again.");
    } finally {
      this.showLoader = false;
      try {
        done?.();
      } catch {
        // ignore
      }
    }
  }

  private getFilenameFromContentDisposition(disposition: string | null): string | null {
    if (!disposition) return null;

    // RFC 5987 / 6266: filename*=UTF-8''...
    const starMatch = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (starMatch?.[1]) {
      try {
        return decodeURIComponent(starMatch[1].trim().replace(/^"|"$/g, ''));
      } catch {
        return starMatch[1].trim().replace(/^"|"$/g, '');
      }
    }

    const match = disposition.match(/filename=("?)([^";]+)\1/i);
    return match?.[2]?.trim() ?? null;
  }

  updateMyDownload(data: any, blob: any) {
    const body = {
      request_id: data?.Request_id,
      fileDownload: {
        filesizeinkb: blob?.size,
        downloads: data?.downloads ? data.downloads + 1 : 1
      }
    }
    this.admnsrv.updateMyDownload(body).subscribe((res: any) => {
      this.getMyDownload();
    }
      , (err) => {
        console.log("Error in updateMyDownload", err)
      })
  }

  updateReportName(name:string): string {
    if (!name) return '';
    let fileName = name.replace(
        /\.(xlsx|xls|xlsm|xlsb|xltx|xltm|json|csv)$/i,
        ""
    );

    const timestampPatterns = [
        /_\d{2}_\d{2}_\d{4}_\d{2}_\d{2}_\d{2}_GMT$/, // 09_07_2026_14_25_25_GMT
        /_\d{14}$/                                     // 20260812082838
    ];

    for (const pattern of timestampPatterns) {
        fileName = fileName.replace(pattern, "");
    }
    return fileName;
  }




}

